import type { IconName } from '../components/NavIcon.tsx';

/**
 * Pure navigation primitives for the mobile chrome (bottom tab bar + app bar),
 * shared by every app that renders it.
 *
 * Deliberately React-free and route-object-free: a tab set and the active-tab
 * resolution are decisions about *pathnames*, so they can be unit-tested without
 * a DOM (this workspace has no component test environment).
 *
 * Only what more than one app needs lives here. Each app keeps its own model
 * next door — the admin app's tab list, route titles, `hideTabBar` and
 * `isDetailRoute` are in `apps/admin/src/lib/mobileNav.ts`, the driver portal's
 * in `apps/portal/src/lib/driverNav.ts` — because those are answers about that
 * app's routes and nothing shared can enumerate them.
 */

/**
 * A tab's stable identity, used for React keys, the active highlight and the
 * badge lookup.
 *
 * A plain string rather than a union of tab names: two apps do not share a tab
 * set, so nothing shared can enumerate the members, and no code switches on the
 * value — `BottomTabBar` looks a key up in a `Record<string, number>` of badge
 * counts and is otherwise indifferent to it.
 */
export type MobileTabKey = string;

export type MobileTab = {
  key: MobileTabKey;
  label: string;
  to: string;
  icon: IconName;
  /** NavLink `end` — when true only an exact pathname match activates the tab. */
  end: boolean;
  /**
   * Extra path prefixes that also activate the tab, for a detail route that
   * doesn't sit under `to` and so can't be inferred from it. No tab needs one
   * today; the field stays because `activeTab` is the shared seam and a future
   * tab whose list and detail routes diverge would otherwise re-add it.
   */
  extraPrefixes: string[];
  /**
   * Count bubble on the tab icon, set on the tab itself. Optional so the admin
   * app, which resolves its counts per render through BottomTabBar's `badges`
   * record, is untouched; the driver portal derives its tabs per driver
   * (driverTabs(badges)) and carries the count here. Absent or 0 renders nothing.
   */
  badge?: number;
  /** Screen-reader noun for the bubble; BottomTabBar's default is "awaiting review". */
  badgeLabel?: string;
};

/**
 * A pathname reduced to the form the comparisons here expect: no trailing
 * slashes, and '' becomes '/'.
 *
 * Exported because every route predicate in both apps needs the same rule —
 * admin's `hideTabBar`, `appBarTitle` and `isDetailRoute`, and the portal's
 * `driverAppBarTitle`. Pathnames are compared as whole segments so /admin/assets
 * never matches a hypothetical /admin/assets-archive, and a trailing slash never
 * changes the answer.
 */
export function normalisePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

// How specifically a tab claims this pathname, or 0 for no claim. Length wins
// ties: /admin/assets/:id is claimed by both Dashboard (were it not `end`) and
// Assets, and the deeper prefix is always the right answer.
function matchStrength(pathname: string, tab: MobileTab): number {
  let best = 0;
  if (tab.end ? pathname === tab.to : isUnder(pathname, tab.to)) best = tab.to.length;
  for (const prefix of tab.extraPrefixes) {
    if (isUnder(pathname, prefix) && prefix.length > best) best = prefix.length;
  }
  return best;
}

/**
 * The tab that owns `pathname`, or null when the route isn't represented on the
 * tab bar (the admin app's Companies, Settings and the rest are reached through
 * More, and no tab should light up for them).
 */
export function activeTab(pathname: string, tabs: MobileTab[]): MobileTab | null {
  const path = normalisePath(pathname);
  let winner: MobileTab | null = null;
  let winnerStrength = 0;
  for (const tab of tabs) {
    const strength = matchStrength(path, tab);
    if (strength > winnerStrength) {
      winner = tab;
      winnerStrength = strength;
    }
  }
  return winner;
}

/**
 * One or two uppercase initials for the avatar circle — first and last name
 * initial, falling back to "U" for an unknown user.
 *
 * Lifted out of the shells, which each carried a private copy, so the sidebar
 * footer, the mobile app bar and the More screen can't drift. Distinct from
 * lib/avatar.ts's `avatarInitials`, which takes the first *two* words and
 * renders "?" for blanks — that one is for company and driver avatars.
 */
export function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}
